import {
  Component,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Input,
  ContentChildren,
  QueryList,
  OnDestroy,
  ElementRef,
  HostListener,
  AfterViewInit,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { PhxBreadcrumbItemComponent } from './breadcrumb-item/phx-breadcrumb-item.component';
import { PhxBreadcrumbSize } from './phx-breadcrumb.types';
import { getWidthsSum, resolveTruncationCount } from './phx-breadcrumb.utils';

/* Fixed widths of medium and small truncated breadcrumb item (3 dots with separator) in pixels */
const MEDIUM_TRUNCATED_BREADCRUMB_WIDTH = 36.3;
const SMALL_TRUNCATED_BREADCRUMB_WIDTH = 20.24;

@Component({
  selector: 'phx-breadcrumb',
  template: `<ng-content></ng-content>`,
  styleUrls: ['./phx-breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-breadcrumb',
    '[class.phx-breadcrumb-medium]': 'phxSize === "medium"',
    '[class.phx-breadcrumb-small]': 'phxSize === "small"',
    '[class.phx-breadcrumb-align-center]': 'phxAlignCenter',
    '[class.phx-breadcrumb-on-bold]': 'phxOnBold',
  },
})
export class PhxBreadcrumbComponent implements AfterViewInit, OnDestroy {
  @Input() public phxSize: PhxBreadcrumbSize = 'medium';
  @Input() public phxAlignCenter: boolean = false;
  @Input() public phxOnBold: boolean = false;
  @ContentChildren(PhxBreadcrumbItemComponent) breadcrumbItems: QueryList<PhxBreadcrumbItemComponent>;

  private readonly _truncatedBreadcrumbItemSize: number = this.phxSize === 'medium' ? MEDIUM_TRUNCATED_BREADCRUMB_WIDTH : SMALL_TRUNCATED_BREADCRUMB_WIDTH;
  private _truncatedBreadcrumbItemCount: number = 0;
  private _previousWidthOfParent: number = 0;
  private _breadcrumbItemWidths: number[] = [];
  private _breadcrumbItemsChangesSubscription: Subscription;

  constructor(private elemRef: ElementRef) {}

  ngAfterViewInit(): void {
    this.setBreadcrumbItemProperties();

    /* Wait for a tick to allow breadcrumb items render properly in order to calculate correct widths of items. */
    setTimeout(() => {
      this.set_BreadcrumbItemWidths();
      this.onBreadcrumbResize();
    });

    this._breadcrumbItemsChangesSubscription = this.breadcrumbItems.changes.subscribe(() => {
      this._truncatedBreadcrumbItemCount = 0;
      this.setBreadcrumbItemProperties();

      /* Wait for a tick to allow breadcrumb items render properly in order to calculate correct widths of items. */
      setTimeout(() => {
        this.set_BreadcrumbItemWidths();
        this.onBreadcrumbResize();
      });
    });
  }

  ngOnDestroy(): void {
    if (this._breadcrumbItemsChangesSubscription) this._breadcrumbItemsChangesSubscription.unsubscribe();
  }

  private setBreadcrumbItemProperties(): void {
    this.breadcrumbItems.forEach((item, index) => {
      item.setBreadcrumbItemProps({
        isDefaultSize: this.phxSize === 'medium',
        isLast: index === this.breadcrumbItems.length - 1,
      });
    });
  }

  private set_BreadcrumbItemWidths(): void {
    const widths: number[] = this.breadcrumbItems.map((item) => this.getElementWidth(item.nativeElement));
    this._breadcrumbItemWidths = widths;
  }

  private getElementWidth(element: Element): number {
    return element.getBoundingClientRect().width;
  }

  private setTruncatedBreadcrumbItemsCount(): void {
    if (!this.breadcrumbItems) return;

    const breadcrumbParentWidth: number = this.getElementWidth(this.elemRef.nativeElement);

    this._truncatedBreadcrumbItemCount = resolveTruncationCount(
      this._truncatedBreadcrumbItemCount,
      breadcrumbParentWidth,
      this._previousWidthOfParent,
      this._breadcrumbItemWidths,
      this._truncatedBreadcrumbItemSize,
      this.breadcrumbItems.length
    );

    this._previousWidthOfParent = breadcrumbParentWidth;
  }

  @HostListener('window:resize')
  public onBreadcrumbResize(): void {
    if (!this.breadcrumbItems) return;

    this.breadcrumbItems.forEach((item, index) => {
      this.setTruncatedBreadcrumbItemsCount();
      item.setBreadcrumbItemProps({ isTruncating: index < this._truncatedBreadcrumbItemCount });
    });
  }
}
